// disciplines.js
// Project disciplines (electrical / structural) and the engineer + firm roster.
//
// A project lists its disciplines in project.disciplines; older projects have no
// list and are treated as electrical only. Each discipline is a sub-project with
// its own Photo Manager, Report Details and Generate Report pages.
//
// The roster lives on the user doc as two lists, edited in Settings:
//   users/{uid}.engineers = [{ id, name, license, qualifications, email, phone }]
//   users/{uid}.firms     = [{ id, name, address, phone, email, logoUrl }]
// Each discipline's Report Details stores the chosen engineerId / firmId.
// buildSignerProfile() turns that choice into the profile shape the PDF
// generators already use (firmName, address, phone, email, logoUrl,
// engineerName, licenseNumber, qualifications).

export const DISCIPLINES = [
  { key: 'electrical', label: 'Electrical' },
  { key: 'structural', label: 'Structural' },
];

export const projectDisciplines = (project) =>
  Array.isArray(project?.disciplines) && project.disciplines.length
    ? DISCIPLINES.map(d => d.key).filter(k => project.disciplines.includes(k))
    : ['electrical'];

// Routes for each discipline's three pages
export const disciplinePaths = (projectId, discipline) => (
  discipline === 'structural'
    ? { photos: `/project/${projectId}/structural/photos`, details: `/project/${projectId}/structural/details`, report: `/project/${projectId}/structural/report` }
    : { photos: `/project/${projectId}/photos`,            details: `/project/${projectId}/electrical`,         report: `/project/${projectId}/report` }
);

// ─── Roster ───────────────────────────────────────────────────────────────────

export const newId = () => Math.random().toString(36).slice(2, 10);

export const EMPTY_ENGINEER = { name: '', license: '', discipline: '', qualifications: '', email: '', phone: '' };
export const EMPTY_FIRM     = { name: '', address: '', phone: '', email: '', logoUrl: '' };

// Roster from the user doc. Accounts set up before the roster existed have a single
// firm/engineer in the old Settings fields; those become the first entries until
// the lists are first saved.
export const rosterFromUser = (user) => {
  const u = user || {};
  let engineers = Array.isArray(u.engineers) ? u.engineers : [];
  let firms     = Array.isArray(u.firms)     ? u.firms     : [];
  if (!Array.isArray(u.engineers) && (u.engineerName || u.licenseNumber)) {
    engineers = [{ ...EMPTY_ENGINEER, id: 'legacy', name: u.engineerName || '', license: u.licenseNumber || '', email: u.email || '', phone: u.phone || '' }];
  }
  if (!Array.isArray(u.firms) && (u.firmName || u.address)) {
    firms = [{ ...EMPTY_FIRM, id: 'legacy', name: u.firmName || '', address: u.address || '', phone: u.phone || '', email: u.email || '', logoUrl: u.logoUrl || '' }];
  }
  return { engineers, firms };
};

// Profile for the PDF generators, from the chosen engineer and firm.
// Missing choices leave those fields blank; nothing falls back to the account owner.
export const buildSignerProfile = (roster, engineerId, firmId) => {
  const e = roster.engineers.find(x => x.id === engineerId) || null;
  const f = roster.firms.find(x => x.id === firmId) || null;
  return {
    engineerName:   e?.name || '',
    licenseNumber:  e?.license || '',
    qualifications: e?.qualifications || '',
    discipline:     e?.discipline || '',
    engineerEmail:  e?.email || '',
    engineerPhone:  e?.phone || '',
    firmName:       f?.name || '',
    address:        f?.address || '',
    phone:          f?.phone || '',
    email:          f?.email || '',
    logoUrl:        f?.logoUrl || '',
    hasEngineer:    !!e,
    hasFirm:        !!f,
  };
};
